import axios from 'axios'
import { useEffect, useRef, useState } from 'react'
import { orderType, rejectionType, statusDetailType } from '../types'
import './UpdateStatus.css'
import { useQuill } from 'react-quilljs'
import 'quill/dist/quill.snow.css'
import { useUser } from '../hooks'
import {
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  MenuItem,
  TextField,
} from '@mui/material'
import { buildStatusDetail } from '../helper'

const getEmailChipColor = (email: string): 'default' | 'warning' | 'error' => {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'error'
  if (!email.toLowerCase().endsWith('@imprestechnology.com')) return 'warning'
  return 'default'
}

function UpdateStatus({
  order,
  setOrder,
  setRejection,
  setStatusDetail,
  setCompleted,
  setOpen,
  open,
}: {
  order: orderType
  setOrder: (order: orderType) => void
  setRejection: (rejection: rejectionType | null) => void
  setStatusDetail: (statusDetail: statusDetailType | null) => void
  setCompleted: (completed: boolean) => void
  setOpen: (open: boolean) => void
  open: boolean
}) {
  const user = useUser()

  const [selectedStatus, setSelectedStatus] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')
  const [emailCCs, setEmailCCs] = useState<Set<string>>(new Set())
  const [emailInput, setEmailInput] = useState('')
  const commentText = useRef('')
  const htmlText = useRef('')
  const { quill, quillRef } = useQuill({ placeholder: 'Comment' })

  const statuses = ['Completed', 'Rejected', 'Hold - Sales', 'Hold - Back Office', 'Canceled']

  const rejectionReasons = [
    'A - Missing document',
    'B - Accessory lines need to be broken out',
    'C - Lines need to be bundled',
    'D - Expired vendor quote',
    'E - Missing information',
    'F - Incorrect information',
    'G - Missing PayTrace information',
    'H - Requires confirmation',
    'I - Missing signature',
  ]

  function setOrderStatus() {
    axios
      .patch(`/order/${order.Id}/status`, {
        status: selectedStatus,
        statusComment: commentText.current,
        htmlComment: htmlText.current,
        rejectionCode: selectedStatus === 'Rejected' ? rejectionReason : null,
        editedBy: user.email,
        emailCCs: Array.from(emailCCs),
      })
      .then(() => {
        const statusComment = commentText.current
        setOrder({
          ...order,
          Status: selectedStatus,
          StatusComment: statusComment,
          Completed: selectedStatus === 'Completed' ? true : false,
          CompletedTimestamp:
            selectedStatus === 'Completed' || selectedStatus === 'Rejected' ? new Date(Date.now()) : null,
        })
        const rejection =
          selectedStatus === 'Rejected'
            ? {
                Id: order.Id,
                Code: rejectionReason,
                Comments: statusComment,
                RejectedBy: user.email,
                Correction: null,
              }
            : null
        setRejection(rejection)
        setStatusDetail(buildStatusDetail(selectedStatus, statusComment, rejection))
        setCompleted(selectedStatus === 'Completed')
        setOpen(false)
      })
      .catch(err => {
        if (err.response) {
          // received error code other than 2xx
          console.error(err.response)
        } else if (err.request) {
          // no response received
          console.error(err.request)
        } else {
          // request was not made
          console.error(err.message)
        }
      })
  }

  useEffect(() => {
    if (!quill) {
      return
    }

    const handleTextChange = () => {
      commentText.current = quill.getText()
      htmlText.current = quill.root.innerHTML
    }

    quill.on('text-change', handleTextChange)
    return () => {
      quill.off('text-change', handleTextChange)
    }
  }, [quill])

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth="md"
      className="update-status-dialog"
      keepMounted
    >
      <DialogTitle>Set Status</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          label="Status"
          value={selectedStatus}
          onChange={e => {
            setSelectedStatus(e.target.value)
          }}
          select
          className="status-select"
        >
          {statuses.map(status => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </TextField>
        <FormGroup style={{ display: selectedStatus === 'Rejected' ? 'flex' : 'none' }}>
          {rejectionReasons.map(reason => (
            <FormControlLabel
              key={reason}
              control={<Checkbox checked={rejectionReason.split(', ').includes(reason)} />}
              label={reason}
              onChange={(_e, checked) => {
                const updatedReasons = checked
                  ? [...rejectionReason.split(', ').filter(r => r), reason]
                  : rejectionReason.split(', ').filter(r => r !== reason)
                setRejectionReason(updatedReasons.join(', '))
              }}
            />
          ))}
        </FormGroup>
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={Array.from(emailCCs)}
          inputValue={emailInput}
          onInputChange={(_, value) => {
            if (value.endsWith(' ') || value.endsWith(',') || value.endsWith(';')) {
              const email = value.slice(0, -1).trim()
              if (email && !emailCCs.has(email)) {
                setEmailCCs(new Set([...emailCCs, email]))
              }
              setEmailInput('')
            } else {
              setEmailInput(value)
            }
          }}
          onChange={(_, newValue) => setEmailCCs(new Set(newValue))}
          renderValue={value =>
            Array.from(value).map(option => (
              <Chip
                className="email-cc-chip"
                size="small"
                label={option}
                color={getEmailChipColor(option)}
                onDelete={() => setEmailCCs(new Set(Array.from(emailCCs).filter(email => email !== option)))}
              />
            ))
          }
          renderInput={params => (
            <TextField
              {...params}
              size="small"
              label="Email CCs"
              helperText="Enter email CCs and press Enter, space, or comma to add"
            />
          )}
          style={{ display: selectedStatus ? 'flex' : 'none' }}
          className="email-cc-input"
        />
        <div>
          <div ref={quillRef} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => setOrderStatus()} disabled={!statuses.includes(selectedStatus)}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export function ScrmHold({
  order,
  setOrder,
  open,
  setOpen,
}: {
  order: orderType
  setOrder: (order: orderType) => void
  open: boolean
  setOpen: (open: boolean) => void
}) {
  const [emailCCs, setEmailCCs] = useState<Set<string>>(new Set())
  const [emailInput, setEmailInput] = useState('')

  const commentText = useRef('')
  const htmlText = useRef('')
  const { quill, quillRef } = useQuill({ placeholder: 'Comment' })

  useEffect(() => {
    if (!quill) {
      return
    }

    const handleTextChange = () => {
      commentText.current = quill.getText()
      htmlText.current = quill.root.innerHTML
    }

    quill.on('text-change', handleTextChange)
    return () => {
      quill.off('text-change', handleTextChange)
    }
  }, [quill])

  const setScrmHold = () => {
    axios
      .patch(`/order/${order.Id}/scrmhold`, {
        scrmStatus: true,
        comment: commentText.current,
        htmlComment: htmlText.current,
        emailCCs: Array.from(emailCCs),
        order: order,
      })
      .then(() => {
        setOrder({
          ...order,
          ScrmHold: true,
          ScrmComment: commentText.current,
        })
        setOpen(false)
      })
      .catch(err => {
        if (err.response) {
          console.error(err.response)
        } else if (err.request) {
          console.error(err.request)
        } else {
          console.error(err.message)
        }
      })
  }

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth="md"
      className="update-status-dialog"
      keepMounted
    >
      <DialogTitle>Set SCRM Hold</DialogTitle>
      <DialogContent>
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={Array.from(emailCCs)}
          inputValue={emailInput}
          onInputChange={(_, value) => {
            if (value.endsWith(' ') || value.endsWith(',') || value.endsWith(';')) {
              const email = value.slice(0, -1).trim()
              if (email && !emailCCs.has(email)) {
                setEmailCCs(new Set([...emailCCs, email]))
              }
              setEmailInput('')
            } else {
              setEmailInput(value)
            }
          }}
          onChange={(_, newValue) => setEmailCCs(new Set(newValue))}
          renderValue={value =>
            Array.from(value).map(option => (
              <Chip
                className="email-cc-chip"
                size="small"
                label={option}
                color={getEmailChipColor(option)}
                onDelete={() => setEmailCCs(new Set(Array.from(emailCCs).filter(email => email !== option)))}
              />
            ))
          }
          renderInput={params => (
            <TextField
              {...params}
              size="small"
              label="Email CCs"
              helperText="Enter email CCs and press Enter, space, or comma to add"
            />
          )}
          className="email-cc-input"
        />
        <div>
          <div ref={quillRef} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => setScrmHold()}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UpdateStatus
